import { useEffect, useState, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { checkDatabaseSeed } from '@/utils/checkDatabaseSeed';



export const useAutoInitialization = () => {
  const [hasContent, setHasContent] = useState(false);
  const [progress, setProgress] = useState({
    currentGrade: '',
    percentage: 0,
    status: 'pending',
    completedGrades: 0,
    totalGrades: 13,
    estimatedTimeRemaining: 0,
    isStale: false,
    retryCount: 0,
  });
  
  const hasTriggered = useRef(false); // Prevent multiple triggers

  useEffect(() => {
    const checkAndTrigger = async () => {
      try {
        // Check initialization status first
        const { data: initStatus } = await supabase
          .from('system_initialization')
          .select('*')
          .eq('process', 'curriculum_generation')
          .maybeSingle();
        
        // Check if process is stale (no heartbeat for 10+ minutes)
        const isStale = initStatus?.last_heartbeat 
          ? (Date.now() - new Date(initStatus.last_heartbeat).getTime()) > 10 * 60 * 1000
          : false;
        
        // Auto-restart stale processes
        if (initStatus?.status === 'in_progress' && isStale) {
          console.log('🔄 Detected stale process, restarting...');
          
          if (hasTriggered.current) {
            console.log('⚠️ Already triggered this session');
            return;
          }
          
          hasTriggered.current = true;
          
          const { error } = await supabase.functions.invoke('initialize-platform');
          
          if (error) {
            console.error('❌ Restart failed:', error);
            hasTriggered.current = false;
          } else {
            console.log('🚀 Stale process restarted automatically!');
          }
          return;
        }
        
        // If generation is pending (even with some content), trigger it automatically
        if (initStatus?.status === 'pending') {
          const metadata = initStatus.metadata as any || {};
          console.log(`📭 Generation pending - starting from Grade ${metadata.starting_from || 'K'}`);
          
          // Only trigger once per session
          if (hasTriggered.current) {
            console.log('⚠️ Already triggered this session');
            return;
          }
          
          hasTriggered.current = true;
          
          // Trigger immediately!
          const { error } = await supabase.functions.invoke('initialize-platform');
          
          if (error) {
            console.error('❌ Trigger failed:', error);
            hasTriggered.current = false; // Allow retry on error
          } else {
            console.log('🚀 Generation started automatically!');
          }
          return;
        }
        
        // If already in progress, just monitor
        if (initStatus?.status === 'in_progress') {
          console.log('⏳ Generation already in progress');
          return;
        }
        
        // Check if content exists
        const { hasContent: contentExists } = await checkDatabaseSeed();
        
        // If completed and content exists, we're done
        if (contentExists && initStatus?.status === 'completed') {
          console.log('✅ All content exists and generation complete');
          setHasContent(true);
          return;
        }
        
        // If no content AND no init status, trigger first time
        if (!contentExists && !initStatus) {
          if (hasTriggered.current) {
            console.log('⚠️ Already triggered this session');
            return;
          }
          
          console.log('📭 No content - triggering first-time generation');
          hasTriggered.current = true;
          
          const { error } = await supabase.functions.invoke('initialize-platform');
          
          if (error) {
            console.error('❌ Trigger failed:', error);
            hasTriggered.current = false; // Allow retry on error
          } else {
            console.log('🚀 Generation started!');
          }
        }
      } catch (error) {
        // Suppress errors for non-critical initialization checks
        hasTriggered.current = false; // Allow retry on error
      }
    };

    const checkProgress = async () => {
      try {
        const { data: initStatus } = await supabase
          .from('system_initialization')
          .select('*')
          .eq('process', 'curriculum_generation')
          .maybeSingle()
          .throwOnError();

        if (initStatus) {
          const metadata = initStatus.metadata as any || {};
          
          // Check for stale heartbeat
          const now = new Date();
          const lastHeartbeat = initStatus.last_heartbeat ? new Date(initStatus.last_heartbeat) : null;
          const isStale = lastHeartbeat && initStatus.status === 'in_progress' && 
            (now.getTime() - lastHeartbeat.getTime()) / 1000 / 60 > 2;

          setProgress({
            currentGrade: metadata.current_grade || '',
            percentage: metadata.percentage || 0,
            status: initStatus.status || 'pending',
            completedGrades: metadata.completed_grades || 0,
            totalGrades: metadata.total_grades || 13,
            estimatedTimeRemaining: metadata.estimated_time_remaining_minutes || 0,
            isStale: isStale || false,
            retryCount: initStatus.retry_count || 0,
          });

          if (initStatus.status === 'completed') {
            setHasContent(true);
          }
        }

        // Also check for actual content
        const { hasContent: contentExists } = await checkDatabaseSeed();
        setHasContent(contentExists);
      } catch (error) {
        // Silently suppress all errors to prevent console pollution during SEO audits
        // This includes 406 errors from system_initialization queries
      }
    };

    // 🎯 CRITICAL: Defer API calls until after initial page render
    // This prevents blocking LCP and improves SEO score
    let interval: ReturnType<typeof setTimeout> | null = null;
    
    const deferredInit = setTimeout(() => {
      checkAndTrigger();
      checkProgress();
      
      // Check every 5 seconds for progress updates
      interval = setInterval(checkProgress, 5000);
    }, 100); // Defer by 100ms to allow page to render first
    
    return () => {
      clearTimeout(deferredInit);
      if (interval) clearInterval(interval);
    };
  }, []);

  return { 
    hasContent, 
    isGenerating: progress.status === 'in_progress',
    progress 
  };
};
